import {ReactNode} from "react";
import {PhoneHQGlyph} from 'ch3-ui-lib';
import {EnsAlertType, EnsLevelInputDto, EnsUserContactListItem, EnsVoicePrompt} from "../../../store/features/ens/types";
import {TypeBadge} from "../components/TypeBadge";
import {ChannelBadge} from "../components/ChannelBadge";
import {getFullName} from "../../../utilis/getFullName";
import {
    formatDuration,
    formatDirectRecipientsWithoutNumber,
    formatTargetSummary,
    hasEligibleEnsNumber,
    levelsRequireExternalMsisdn,
    SubmitBlockedError,
} from "./utils";

interface Props {
    state: {
        type: EnsAlertType | null;
        name: string;
        message: string;
        targetGroupIds: number[];
        targetUsers: EnsUserContactListItem[];
        levels: EnsLevelInputDto[];
        voicePrompt: EnsVoicePrompt | null;
    };
    submitError: SubmitBlockedError | null;
}

const ReviewRow = ({label, children}: { label: string; children: ReactNode }) => (
    <div className="grid grid-cols-[120px_1fr] items-center gap-3 border-b border-line py-2.5 last:border-b-0">
        <div className="text-sm text-ink-muted">{label}</div>
        <div className="text-sm font-medium text-ink">{children}</div>
    </div>
);

export const Step4Review = ({state, submitError}: Props) => {
    const groupCount = state.targetGroupIds.length;
    const userCount = state.targetUsers.length;
    const externalNumberRequired = levelsRequireExternalMsisdn(state.levels);
    const usersWithoutNumber = externalNumberRequired
        ? state.targetUsers.filter((user) => !hasEligibleEnsNumber(user))
        : [];

    return (
        <div className="flex flex-col gap-4">
            <div>
                <h3 className="text-sm font-semibold text-ink">Review &amp; send</h3>
                <p className="mt-1 text-sm leading-5 text-ink-muted">
                    Make sure everything is correct. Sending the alert will start the escalation immediately.
                </p>
            </div>

            {submitError && (
                <div className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm leading-5 text-red-800">
                    <PhoneHQGlyph name="error" size={16} className="mt-0.5"/>
                    <span>
                        <span className="font-semibold">The alert was not sent.</span>{' '}
                        {submitError.unreachableRecipients.length > 0
                            ? `These recipients have no eligible emergency phone number: ${
                                submitError.unreachableRecipients.map((recipient) => recipient.displayName).join(', ')
                            }. Add their numbers in ENS settings or remove SMS and phone-call steps.`
                            : submitError.message}
                    </span>
                </div>
            )}

            {!submitError && externalNumberRequired && (usersWithoutNumber.length > 0 || groupCount > 0) && (
                <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm leading-5 text-amber-800">
                    <PhoneHQGlyph name="phone_disabled" size={16} className="mt-0.5"/>
                    <span>
                        This alert uses SMS or phone-call escalation, so every recipient needs an eligible emergency phone number.{' '}
                        {usersWithoutNumber.length > 0 && (
                            <>
                                {formatDirectRecipientsWithoutNumber(usersWithoutNumber.length, userCount)}{' '}
                                Affected:{' '}
                                <span className="font-semibold">
                                    {usersWithoutNumber
                                        .map((user) => getFullName(user.firstName, user.lastName) || user.email)
                                        .join(', ')}
                                </span>.{' '}
                            </>
                        )}
                        {groupCount > 0 && (
                            <>
                                {usersWithoutNumber.length > 0
                                    ? 'Group members are not included in this count. They are expanded and validated when the alert is sent.'
                                    : 'Group members are expanded and validated when the alert is sent.'}
                            </>
                        )}
                    </span>
                </div>
            )}

            <div className="rounded-lg border border-line bg-surface px-4 py-2">
                <ReviewRow label="Type">
                    {state.type ? <TypeBadge type={state.type} variant="dotText"/> : '—'}
                </ReviewRow>
                <ReviewRow label="Alert name">
                    <span className="font-semibold">{state.name || '—'}</span>
                </ReviewRow>
                <ReviewRow label="Target">
                    {formatTargetSummary(groupCount, userCount)}
                </ReviewRow>
                {state.levels.map((level, idx) => (
                    <ReviewRow key={level.sequenceNo} label={`L${level.sequenceNo}`}>
                        <span className="inline-flex items-center gap-2">
                            <ChannelBadge channel={level.channel}/>
                            <span className="text-ink-muted">
                                {idx === 0 ? '· immediate' : `· after ${formatDuration(level.delaySecondsFromPrevious)}`}
                            </span>
                        </span>
                    </ReviewRow>
                ))}
                <ReviewRow label="Voice prompt">
                    {state.voicePrompt?.originalFileName ?? '—'}
                </ReviewRow>
            </div>

            <div>
                <div className="mb-1 text-sm text-ink-muted">Message</div>
                <div className="whitespace-pre-line rounded-lg border border-line bg-zinc-50 p-3 text-sm leading-5 text-ink">
                    {state.message || '—'}
                </div>
            </div>
        </div>
    );
};
